// ไฟล์: frontend/src/pages/teacher/Reports.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';
import Chart from 'chart.js/auto';

function TeacherReports() {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [students, setStudents] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reportMode, setReportMode] = useState('class'); // 'class' หรือ 'student'
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [studentAttendance, setStudentAttendance] = useState([]);
  const [attendanceChart, setAttendanceChart] = useState(null);
  
  useEffect(() => {
    // เริ่มต้นให้เลือกวันที่ย้อนหลัง 30 วัน
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setEndDate(formatDate(today));
    setStartDate(formatDate(thirtyDaysAgo));
    
    fetchClasses();
  }, []);
  
  useEffect(() => {
    // เมื่อเลือกชั้นเรียนใหม่ ให้โหลดรายชื่อนักเรียนในชั้นเรียนนั้น
    if (selectedClassId) {
      fetchStudentsInClass(selectedClassId);
      fetchAttendanceSummary();
    }
  }, [selectedClassId, startDate, endDate]);
  
  useEffect(() => {
    // เมื่อเลือกนักเรียนใหม่ ให้โหลดข้อมูลการเข้าเรียนของนักเรียนคนนั้น
    if (reportMode === 'student' && selectedStudentId) {
      fetchStudentAttendance();
    }
  }, [reportMode, selectedStudentId, startDate, endDate]);
  
  useEffect(() => {
    // สร้างแผนภูมิสรุป
    if (reportMode === 'class' && summaryData.length > 0) {
      createClassChart();
    } else if (reportMode === 'student' && studentAttendance.length > 0) {
      createStudentChart();
    }
    
    // Cleanup function
    return () => {
      if (attendanceChart) {
        attendanceChart.destroy();
        setAttendanceChart(null);
      }
    };
  }, [reportMode, summaryData, studentAttendance]);
  
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const fetchClasses = async () => {
    try {
      setIsLoading(true);
      const response = await api.class.getMyClasses();
      const myClasses = response.data.data || [];
      setClasses(myClasses);
      
      // เลือกชั้นเรียนแรกเป็นค่าเริ่มต้น
      if (myClasses.length > 0) {
        setSelectedClassId(myClasses[0].id);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลชั้นเรียนได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchStudentsInClass = async (classId) => {
    try {
      const response = await api.student.getStudentsByClass(classId);
      setStudents(response.data.data || []);
      
      // เลือกนักเรียนคนแรกเป็นค่าเริ่มต้น
      if (response.data.data && response.data.data.length > 0) {
        setSelectedStudentId(response.data.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };
  
  const fetchAttendanceSummary = async () => {
    if (!selectedClassId || !startDate || !endDate) return;
    
    try {
      setIsLoading(true);
      const response = await api.attendance.getAttendanceSummaryByClass(
        selectedClassId,
        startDate,
        endDate
      );
      setSummaryData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching attendance summary:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลสรุปการเข้าเรียนได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchStudentAttendance = async () => {
    if (!selectedStudentId || !startDate || !endDate) return;
    
    try {
      setIsLoading(true);
      const response = await api.attendance.getAttendanceByStudent(
        selectedStudentId,
        startDate,
        endDate
      );
      setStudentAttendance(response.data.data || []);
    } catch (error) {
      console.error('Error fetching student attendance:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลการเข้าเรียนของนักเรียนได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const createClassChart = () => {
    // หาข้อมูลสำหรับแผนภูมิ
    const totalPresent = summaryData.reduce((sum, student) => sum + (student.present_count || 0), 0);
    const totalAbsent = summaryData.reduce((sum, student) => sum + (student.absent_count || 0), 0);
    const totalSickLeave = summaryData.reduce((sum, student) => sum + (student.sick_leave_count || 0), 0);
    const totalPersonalLeave = summaryData.reduce((sum, student) => sum + (student.personal_leave_count || 0), 0);
    const totalLate = summaryData.reduce((sum, student) => sum + (student.late_count || 0), 0);
    
    // สร้างแผนภูมิ
    const ctx = document.getElementById('attendanceChart');
    
    // ทำลายแผนภูมิเก่า (ถ้ามี)
    if (attendanceChart) {
      attendanceChart.destroy();
    }
    
    // สร้างแผนภูมิใหม่
    const newChart = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: ['มาเรียน', 'ขาดเรียน', 'ลาป่วย', 'ลากิจ', 'มาสาย'],
        datasets: [{
          data: [totalPresent, totalAbsent, totalSickLeave, totalPersonalLeave, totalLate],
          backgroundColor: [
            '#28a745', // เขียว - มาเรียน
            '#dc3545', // แดง - ขาดเรียน
            '#17a2b8', // ฟ้า - ลาป่วย
            '#6c757d', // เทา - ลากิจ
            '#ffc107'  // เหลือง - มาสาย
          ],
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'สรุปการเข้าเรียนทั้งชั้นเรียน'
          }
        }
      }
    });
    
    setAttendanceChart(newChart);
  };
  
  const createStudentChart = () => {
    const dates = [];
    const statuses = [];
    
    // จัดเรียงข้อมูลตามวันที่
    const sortedAttendance = [...studentAttendance].sort((a, b) => {
      return new Date(a.attendance_date) - new Date(b.attendance_date);
    });
    
    // เตรียมข้อมูลสำหรับแผนภูมิ
    sortedAttendance.forEach(record => {
      const date = new Date(record.attendance_date);
      const formattedDate = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
      dates.push(formattedDate);
      
      // แปลงสถานะเป็นตัวเลขสำหรับแผนภูมิ
      let statusValue;
      switch(record.status) {
        case 'present': statusValue = 5; break;
        case 'late': statusValue = 4; break;
        case 'sick_leave': statusValue = 3; break;
        case 'personal_leave': statusValue = 2; break;
        case 'absent': statusValue = 1; break;
        default: statusValue = 0;
      }
      
      statuses.push(statusValue);
    });
    
    // สร้างแผนภูมิ
    const ctx = document.getElementById('attendanceChart');
    
    // ทำลายแผนภูมิเก่า (ถ้ามี)
    if (attendanceChart) {
      attendanceChart.destroy();
    }
    
    // สร้างแผนภูมิใหม่
    const newChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: dates,
        datasets: [{
          label: 'การเข้าเรียน',
          data: statuses,
          backgroundColor: 'rgba(255, 107, 1, 0.2)',
          borderColor: 'rgba(255, 107, 1, 1)',
          borderWidth: 2,
          pointBackgroundColor: 'rgba(255, 107, 1, 1)',
          tension: 0.3
        }]
      },
      options: {
        responsive: true,
        scales: {
          y: {
            beginAtZero: true,
            max: 5,
            ticks: {
              stepSize: 1,
              callback: function(value) {
                switch(value) {
                  case 5: return 'มาเรียน';
                  case 4: return 'มาสาย';
                  case 3: return 'ลาป่วย';
                  case 2: return 'ลากิจ';
                  case 1: return 'ขาดเรียน';
                  case 0: return '';
                  default: return '';
                }
              }
            }
          },
          x: {
            ticks: {
              maxRotation: 45,
              minRotation: 45
            }
          }
        },
        plugins: {
          title: {
            display: true,
            text: 'ประวัติการเข้าเรียนรายบุคคล'
          }
        }
      }
    });
    
    setAttendanceChart(newChart);
  };
  
  // แปลงสถานะเป็นป้ายสี
  const getStatusBadge = (status) => {
    switch(status) {
      case 'present':
        return <span className="badge badge-success">มาเรียน</span>;
      case 'absent':
        return <span className="badge badge-danger">ขาดเรียน</span>;
      case 'sick_leave':
        return <span className="badge badge-info">ลาป่วย</span>;
      case 'personal_leave':
        return <span className="badge badge-secondary">ลากิจ</span>;
      case 'late':
        return <span className="badge badge-warning">มาสาย</span>;
      default:
        return <span className="badge badge-light">ไม่ระบุ</span>;
    }
  };
  
  // สรุปจำนวนสถานะสำหรับนักเรียนคนหนึ่ง
  const getStudentSummary = (studentId) => {
    const student = summaryData.find(s => s.student_id === studentId);
    if (!student) return null;
    
    const total = (student.present_count || 0) +
                 (student.absent_count || 0) +
                 (student.sick_leave_count || 0) +
                 (student.personal_leave_count || 0) +
                 (student.late_count || 0);
    
    const presentPercent = total > 0 ? ((student.present_count || 0) / total * 100).toFixed(1) : 0;
    
    return {
      present: student.present_count || 0,
      absent: student.absent_count || 0,
      sick_leave: student.sick_leave_count || 0,
      personal_leave: student.personal_leave_count || 0,
      late: student.late_count || 0,
      total: total,
      presentPercent: presentPercent
    };
  };
  
  // คำนวณเปอร์เซ็นต์การมาเรียนทั้งชั้น
  const getClassAttendancePercent = () => {
    if (summaryData.length === 0) return 0;
    
    let totalPresent = 0;
    let totalAll = 0;
    
    summaryData.forEach(student => {
      totalPresent += student.present_count || 0;
      totalAll += (student.present_count || 0) +
                 (student.absent_count || 0) +
                 (student.sick_leave_count || 0) +
                 (student.personal_leave_count || 0) +
                 (student.late_count || 0);
    });
    
    return totalAll > 0 ? (totalPresent / totalAll * 100).toFixed(1) : 0;
  };
  
  // ฟังก์ชันออกรายงาน PDF (จำลอง)
  const exportReport = () => {
    Swal.fire({
      title: 'ออกรายงาน',
      text: 'กำลังเตรียมไฟล์ PDF...',
      icon: 'info',
      timer: 2000,
      showConfirmButton: false
    }).then(() => {
      Swal.fire({
        icon: 'success',
        title: 'ออกรายงานสำเร็จ',
        text: 'รายงานถูกบันทึกลงคอมพิวเตอร์ของคุณแล้ว',
        confirmButtonColor: '#ff6b01'
      });
    });
  };
  
  return (
    <div className="wrapper">
      <Header />
      <Sidebar />
      
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">รายงานการเข้าเรียน</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <a href="/teacher/dashboard">หน้าหลัก</a>
                  </li>
                  <li className="breadcrumb-item active">รายงาน</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        
        <section className="content">
          <div className="container-fluid">
            {isLoading ? (
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            ) : (
              <>
                {/* ตัวเลือกรายงาน */}
                <div className="card card-orange">
                  <div className="card-header">
                    <h3 className="card-title">ตัวเลือกรายงาน</h3>
                  </div>
                  <div className="card-body">
                    {classes.length === 0 ? (
                      <div className="alert alert-warning">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        คุณยังไม่ได้รับมอบหมายให้ดูแลชั้นเรียนใดๆ กรุณาติดต่อผู้ดูแลระบบ
                      </div>
                    ) : (
                      <div className="row">
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>ชั้นเรียน</label>
                            <select
                              className="form-control"
                              value={selectedClassId}
                              onChange={(e) => setSelectedClassId(e.target.value)}
                            >
                              {classes.map(classItem => (
                                <option key={classItem.id} value={classItem.id}>
                                  {classItem.class_name} (ห้อง {classItem.room_number})
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>ประเภทรายงาน</label>
                            <select
                              className="form-control"
                              value={reportMode}
                              onChange={(e) => setReportMode(e.target.value)}
                            >
                              <option value="class">รายงานทั้งชั้น</option>
                              <option value="student">รายงานรายบุคคล</option>
                            </select>
                          </div>
                        </div>
                        {reportMode === 'student' && (
                          <div className="col-md-3">
                            <div className="form-group">
                              <label>นักเรียน</label>
                              <select
                                className="form-control"
                                value={selectedStudentId}
                                onChange={(e) => setSelectedStudentId(e.target.value)}
                              >
                                {students.map(student => (
                                  <option key={student.id} value={student.id}>
                                    {student.first_name} {student.last_name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>วันที่เริ่มต้น</label>
                            <input
                              type="date"
                              className="form-control"
                              value={startDate}
                              onChange={(e) => setStartDate(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>วันที่สิ้นสุด</label>
                            <input
                              type="date"
                              className="form-control"
                              value={endDate}
                              onChange={(e) => setEndDate(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="col-md-3 d-flex align-items-end">
                          <button
                            className="btn btn-orange"
                            onClick={reportMode === 'class' ? fetchAttendanceSummary : fetchStudentAttendance}
                          >
                            <i className="fas fa-search mr-2"></i>
                            แสดงรายงาน
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* แสดงรายงาน */}
                {reportMode === 'class' && summaryData.length > 0 && (
                  <div className="row">
                    <div className="col-md-8">
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">รายงานการเข้าเรียนทั้งชั้น</h3>
                          <div className="card-tools">
                            <button className="btn btn-tool" onClick={exportReport}>
                              <i className="fas fa-file-pdf"></i> ออกรายงาน PDF
                            </button>
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table table-bordered table-striped">
                              <thead>
                                <tr>
                                  <th style={{ width: '40px' }}>ลำดับ</th>
                                  <th>ชื่อ-นามสกุล</th>
                                  <th style={{ width: '70px' }}>มาเรียน</th>
                                  <th style={{ width: '70px' }}>ขาดเรียน</th>
                                  <th style={{ width: '70px' }}>ลาป่วย</th>
                                  <th style={{ width: '70px' }}>ลากิจ</th>
                                  <th style={{ width: '70px' }}>มาสาย</th>
                                  <th style={{ width: '80px' }}>เปอร์เซ็นต์</th>
                                </tr>
                              </thead>
                              <tbody>
                                {summaryData.map((student, index) => {
                                  const summary = getStudentSummary(student.student_id);
                                  return (
                                    <tr key={student.student_id}>
                                      <td>{index + 1}</td>
                                      <td>
                                        {student.first_name} {student.last_name}
                                        <small className="d-block text-muted">
                                          รหัส: {student.student_code}
                                        </small>
                                      </td>
                                      <td className="text-center">{summary.present}</td>
                                      <td className="text-center">{summary.absent}</td>
                                      <td className="text-center">{summary.sick_leave}</td>
                                      <td className="text-center">{summary.personal_leave}</td>
                                      <td className="text-center">{summary.late}</td>
                                      <td className="text-center">
                                        <span className={`badge ${summary.presentPercent >= 80 ? 'bg-success' : 'bg-danger'}`}>
                                          {summary.presentPercent}%
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr className="bg-light">
                                  <th colSpan="2">รวมทั้งชั้น</th>
                                  <th className="text-center">
                                    {summaryData.reduce((sum, student) => sum + (student.present_count || 0), 0)}
                                  </th>
                                  <th className="text-center">
                                    {summaryData.reduce((sum, student) => sum + (student.absent_count || 0), 0)}
                                  </th>
                                  <th className="text-center">
                                    {summaryData.reduce((sum, student) => sum + (student.sick_leave_count || 0), 0)}
                                  </th>
                                  <th className="text-center">
                                    {summaryData.reduce((sum, student) => sum + (student.personal_leave_count || 0), 0)}
                                  </th>
                                  <th className="text-center">
                                    {summaryData.reduce((sum, student) => sum + (student.late_count || 0), 0)}
                                  </th>
                                  <th className="text-center">
                                    <span className={`badge ${getClassAttendancePercent() >= 80 ? 'bg-success' : 'bg-danger'}`}>
                                      {getClassAttendancePercent()}%
                                    </span>
                                  </th>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">แผนภูมิการเข้าเรียน</h3>
                        </div>
                        <div className="card-body">
                          <canvas id="attendanceChart"></canvas>
                        </div>
                      </div>
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">สรุปการเข้าเรียน</h3>
                        </div>
                        <div className="card-body p-0">
                          <table className="table">
                            <tbody>
                              <tr>
                                <th>เปอร์เซ็นต์การมาเรียนเฉลี่ย</th>
                                <td>
                                  <span className={`badge ${getClassAttendancePercent() >= 80 ? 'bg-success' : 'bg-danger'}`}>
                                    {getClassAttendancePercent()}%
                                  </span>
                                </td>
                              </tr>
                              <tr>
                                <th>จำนวนนักเรียนทั้งหมด</th>
                                <td>{summaryData.length} คน</td>
                              </tr>
                              <tr>
                                <th>จำนวนวัน</th>
                                <td>
                                  {Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24) + 1)} วัน
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {reportMode === 'student' && studentAttendance.length > 0 && (
                  <div className="row">
                    <div className="col-md-8">
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">
                            รายงานการเข้าเรียนรายบุคคล: {students.find(s => s.id === parseInt(selectedStudentId))?.first_name} {students.find(s => s.id === parseInt(selectedStudentId))?.last_name}
                          </h3>
                          <div className="card-tools">
                            <button className="btn btn-tool" onClick={exportReport}>
                              <i className="fas fa-file-pdf"></i> ออกรายงาน PDF
                            </button>
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table table-bordered table-striped">
                              <thead>
                                <tr>
                                  <th style={{ width: '40px' }}>ลำดับ</th>
                                  <th>วันที่</th>
                                  <th>ชั้นเรียน</th>
                                  <th>สถานะ</th>
                                  <th>บันทึกโดย</th>
                                </tr>
                              </thead>
                              <tbody>
                                {studentAttendance.length === 0 ? (
                                  <tr>
                                    <td colSpan="5" className="text-center">
                                      ไม่พบข้อมูลการเข้าเรียนในช่วงวันที่ที่เลือก
                                    </td>
                                  </tr>
                                ) : (
                                  studentAttendance.map((record, index) => (
                                    <tr key={record.id}>
                                      <td>{index + 1}</td>
                                      <td>
                                        {new Date(record.attendance_date).toLocaleDateString('th-TH', {
                                          year: 'numeric',
                                          month: 'long',
                                          day: 'numeric',
                                        })}
                                      </td>
                                      <td>{record.class_name}</td>
                                      <td>{getStatusBadge(record.status)}</td>
                                      <td>{record.recorded_by_name}</td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">แผนภูมิการเข้าเรียน</h3>
                        </div>
                        <div className="card-body">
                          <canvas id="attendanceChart"></canvas>
                        </div>
                      </div>
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">สรุปการเข้าเรียน</h3>
                        </div>
                        <div className="card-body p-0">
                          <table className="table">
                            <tbody>
                              <tr>
                                <th>มาเรียน</th>
                                <td>
                                  {studentAttendance.filter(r => r.status === 'present').length} ครั้ง
                                </td>
                              </tr>
                              <tr>
                                <th>ขาดเรียน</th>
                                <td>
                                  {studentAttendance.filter(r => r.status === 'absent').length} ครั้ง
                                </td>
                              </tr>
                              <tr>
                                <th>ลาป่วย</th>
                                <td>
                                  {studentAttendance.filter(r => r.status === 'sick_leave').length} ครั้ง
                                </td>
                              </tr>
                              <tr>
                                <th>ลากิจ</th>
                                <td>
                                  {studentAttendance.filter(r => r.status === 'personal_leave').length} ครั้ง
                                </td>
                              </tr>
                              <tr>
                                <th>มาสาย</th>
                                <td>
                                  {studentAttendance.filter(r => r.status === 'late').length} ครั้ง
                                </td>
                              </tr>
                              <tr>
                                <th>เปอร์เซ็นต์การมาเรียน</th>
                                <td>
                                  {studentAttendance.length > 0 ? (
                                    <span className={`badge ${(studentAttendance.filter(r => r.status === 'present').length / studentAttendance.length * 100) >= 80 ? 'bg-success' : 'bg-danger'}`}>
                                      {(studentAttendance.filter(r => r.status === 'present').length / studentAttendance.length * 100).toFixed(1)}%
                                    </span>
                                  ) : (
                                    <span className="badge bg-secondary">0%</span>
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
      
      <Footer />
    </div>
  );
}

export default TeacherReports;